/**
 * Behaviour for the combined region & language selector.
 *
 * Handles three concerns, each independent so a page can use any combination:
 *   1. [data-locale-selector]  - the shared fields (snippets/locale-selector-fields.liquid).
 *                                Enables Apply only once something changes, then copies both
 *                                selections into the native form's hidden inputs and submits once.
 *   2. [data-locale-dropdown]  - an anchored panel (header), with click-outside and Escape.
 *   3. [data-locale-modal]     - a centred modal (footer), opened by any [data-locale-selector-open].
 *
 * Loaded with `defer` and guarded, so rendering the snippet more than once per page is harmless.
 */
(function () {
  'use strict';

  if (window.__localeSelectorInit) return;
  window.__localeSelectorInit = true;

  /* ------------------------------------------------------------------ *
   * 1. Fields: gate Apply, then commit both values in one submit
   * ------------------------------------------------------------------ */

  function initFields(root) {
    if (root.dataset.localeSelectorBound) return;
    root.dataset.localeSelectorBound = 'true';

    var form = root.querySelector('form');
    var countryInput = root.querySelector('[data-country-input]');
    var localeInput = root.querySelector('[data-locale-input]');
    var regionSelect = root.querySelector('[data-region-select]');
    var languageSelect = root.querySelector('[data-language-select]');
    var submitBtn = root.querySelector('[data-locale-submit]');

    if (!form) return;

    var initialCountry = countryInput ? countryInput.value : '';
    var initialLocale = localeInput ? localeInput.value : '';

    function refreshSubmitState() {
      if (!submitBtn) return;
      var changed =
        (regionSelect && regionSelect.value !== initialCountry) ||
        (languageSelect && languageSelect.value !== initialLocale);
      submitBtn.disabled = !changed;
    }

    if (regionSelect) regionSelect.addEventListener('change', refreshSubmitState);
    if (languageSelect) languageSelect.addEventListener('change', refreshSubmitState);

    // Shopify handles all market and locale URL routing from these two values.
    form.addEventListener('submit', function () {
      if (regionSelect && countryInput) countryInput.value = regionSelect.value;
      if (languageSelect && localeInput) localeInput.value = languageSelect.value;
      if (submitBtn) submitBtn.disabled = true;
    });

    refreshSubmitState();
  }

  /* ------------------------------------------------------------------ *
   * 2. Dropdown: anchored panel under a header control
   * ------------------------------------------------------------------ */

  function initDropdown(root) {
    if (root.dataset.localeDropdownBound) return;
    root.dataset.localeDropdownBound = 'true';

    var toggle = root.querySelector('[data-locale-dropdown-toggle]');
    var panel = root.querySelector('[data-locale-dropdown-panel]');
    if (!toggle || !panel) return;

    function isOpen() {
      return !panel.hasAttribute('hidden');
    }

    function open() {
      panel.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
    }

    function close(returnFocus) {
      if (!isOpen()) return;
      panel.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
      if (returnFocus) toggle.focus();
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen()) close(false);
      else open();
    });

    document.addEventListener('click', function (e) {
      if (isOpen() && !root.contains(e.target)) close(false);
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) {
        e.stopPropagation();
        close(true);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Modal: centred dialog, openable from anywhere on the page
   * ------------------------------------------------------------------ */

  function initModal(modal) {
    if (modal.dataset.localeModalBound) return;
    modal.dataset.localeModalBound = 'true';

    var closeBtn = modal.querySelector('[data-locale-modal-close]');
    var overlay = modal.querySelector('[data-locale-modal-overlay]');
    var lastOpener = null;

    // Move to <body> so position:fixed escapes the footer's containment context.
    document.body.appendChild(modal);

    function isOpen() {
      return modal.getAttribute('aria-hidden') === 'false';
    }

    function open(opener) {
      lastOpener = opener || null;
      modal.setAttribute('aria-hidden', 'false');
      if (lastOpener) lastOpener.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      if (!isOpen()) return;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastOpener) {
        lastOpener.setAttribute('aria-expanded', 'false');
        lastOpener.focus();
      }
    }

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });

    // Delegated so openers rendered before the modal (e.g. the header) still work.
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-locale-selector-open]');
      if (!opener) return;
      e.preventDefault();
      open(opener);
    });

    document.documentElement.setAttribute('data-locale-modal-ready', '');
  }

  /* ------------------------------------------------------------------ */

  function init() {
    document.querySelectorAll('[data-locale-selector]').forEach(initFields);
    document.querySelectorAll('[data-locale-dropdown]').forEach(initDropdown);
    document.querySelectorAll('[data-locale-modal]').forEach(initModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind after Theme Editor section re-renders.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
